/**
 * Turns a stream of pointer positions (in world disk coordinates, anywhere in the plane) into
 * strokes stored in domain coordinates.
 *
 * The first point is folded into the rotation domain W, giving a group element g; later points
 * are expressed in the same frame g(w), so the stroke stays continuous even when it wanders
 * across mirrors or tile edges. When it strays too far from W the capture starts a new piece in
 * a re-folded frame (the raster draws every piece under the whole group, so the seam is
 * invisible).
 */
import type { Complex } from '../geometry/complex';
import { distance, geodesicPoint } from '../geometry/hyperbolic';
import { type Isometry, apply } from '../geometry/mobius';
import { type TriangleGroup, foldToIsometry } from '../geometry/triangle-group';
import { type DomainPoint, nearRotationDomain, toDomain } from './domain';
import { type Stroke, type StrokeRole, type Symmetry, simplifyStroke } from './motif';

/** How far (domain units) a stroke may leave W before its frame is re-folded. */
const SLACK = 0.3;
/** Longest step between captured points, as a fraction of the hyperbolic length PR. */
const STEP = 0.06;

export interface CaptureOptions {
  role: StrokeRole;
  size: number;
  symmetry: Symmetry;
  /** Called whenever points are appended: the stroke piece and the index of its first new point. */
  onAppend?: (piece: Stroke, firstNew: number) => void;
}

export class StrokeCapture {
  private readonly tg: TriangleGroup;
  private readonly opts: CaptureOptions;
  private pieces: Stroke[] = [];
  private frame: Isometry | null = null;
  private lastWorld: Complex | null = null;
  private lastWidth = 1;

  constructor(tg: TriangleGroup, opts: CaptureOptions) {
    this.tg = tg;
    this.opts = opts;
  }

  get started(): boolean {
    return this.lastWorld !== null;
  }

  private newPiece(points: DomainPoint[], widths: number[]): Stroke {
    const piece: Stroke = {
      role: this.opts.role,
      symmetry: this.opts.symmetry,
      size: this.opts.size,
      points,
      widths,
    };
    this.pieces.push(piece);
    return piece;
  }

  /** Adds a world point. `width` in (0, 1] scales the brush (pen pressure). */
  add(world: Complex, width = 1): void {
    const w = Math.max(0.2, Math.min(1, width));
    if (!this.lastWorld || !this.frame) {
      const { g } = foldToIsometry(this.tg, world, true);
      this.frame = g;
      const piece = this.newPiece([toDomain(this.tg, apply(g, world))], [w]);
      this.lastWorld = world;
      this.lastWidth = w;
      this.opts.onAppend?.(piece, 0);
      return;
    }
    const d = distance(this.lastWorld, world);
    const maxStep = STEP * this.tg.lengthPR;
    const steps = Math.max(1, Math.ceil(d / maxStep));
    const piece0 = this.pieces[this.pieces.length - 1];
    const firstNew = piece0.points.length;
    let piece = piece0;
    let pieceFirstNew = firstNew;
    const start = this.lastWorld;
    for (let k = 1; k <= steps; k++) {
      const p = steps === 1 ? world : geodesicPoint(start, world, k / steps);
      const wk = this.lastWidth + ((w - this.lastWidth) * k) / steps;
      const dp = toDomain(this.tg, apply(this.frame, p));
      if (nearRotationDomain(dp, SLACK)) {
        piece.points.push(dp);
        piece.widths!.push(wk);
      } else {
        // flush what we have, then continue in a re-folded frame
        if (piece.points.length > pieceFirstNew) {
          this.opts.onAppend?.(piece, Math.max(0, pieceFirstNew - 1));
        }
        const prev = k === 1 ? start : geodesicPoint(start, world, (k - 1) / steps);
        const { g } = foldToIsometry(this.tg, p, true);
        this.frame = g;
        const prevWidth = this.lastWidth + ((w - this.lastWidth) * (k - 1)) / steps;
        piece = this.newPiece(
          [toDomain(this.tg, apply(g, prev)), toDomain(this.tg, apply(g, p))],
          [prevWidth, wk],
        );
        pieceFirstNew = 0;
      }
    }
    if (piece.points.length > pieceFirstNew) {
      // include the joint point so the incremental draw connects to what was already drawn
      this.opts.onAppend?.(piece, Math.max(0, pieceFirstNew - 1));
    }
    this.lastWorld = world;
    this.lastWidth = w;
  }

  /** Ends the stroke; returns its pieces, simplified, with constant widths dropped. */
  finish(): Stroke[] {
    const out = this.pieces.map((s) => {
      const constant = !s.widths || s.widths.every((x) => Math.abs(x - s.widths![0]) < 1e-3);
      const base: Stroke = constant
        ? { ...s, size: s.size * (s.widths?.[0] ?? 1), widths: undefined }
        : s;
      return simplifyStroke(base, 0.0025);
    });
    this.pieces = [];
    this.frame = null;
    this.lastWorld = null;
    return out;
  }

  /** The pieces captured so far (not simplified). */
  get current(): readonly Stroke[] {
    return this.pieces;
  }
}
